/* eslint-disable jsx-a11y/anchor-is-valid */

import ja from 'date-fns/locale/ja';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import MicroModal from 'micromodal';
import { FC, Fragment, useEffect, useState } from 'react';
import { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useHistory } from 'react-router-dom';
import { getModalOption, Setting, statusMessages } from '../App';
import { Input, Label, Textarea } from '../components/CancelModalCss';
import { useSubscriptionDetailCancel } from '../hooks/use-subscription-cancel';
import base from '../styles/Base.module.css';
import style from '../styles/SubscriptionCancel.module.css';

registerLocale('ja', ja);

export type StatusMessage = keyof typeof statusMessages;

dayjs.extend(weekday);

export const SubscriptionCancel: FC<{ setting: Setting }> = ({ setting }) => {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    window.parent.postMessage({ action: 'subscriptionCancel' }, '*');
  }, []);

  const { subscription, updateStatus, cancelSurveySetting, cancelSurveyInput, setCancelSurveyInput } =
    useSubscriptionDetailCancel(setting);

  const reactRouterHistory = useHistory();

  if (subscription && subscription.status === 'CANCELLED') {
    return (
      <>
        <h2 className={style.cancelHeading}>定期便の解約</h2>
        <main id="modal-cancelled-content">
          <p>既に定期購買は解約されています。</p>
        </main>
      </>
    );
  }

  // 解約時アンケートにチェックが入っていて、理由の選択肢が一つ以上設定されていれば解約アンケートが有効
  const cancelSurveyEnable =
    cancelSurveySetting.enable && cancelSurveySetting.reasons && cancelSurveySetting.reasons.length > 0 ? true : false;

  const setCancelReason = (reason: string) => {
    const target = reason === cancelSurveyInput.selected ? undefined : reason;
    setCancelSurveyInput({ ...cancelSurveyInput, selected: target });

    reason === cancelSurveyInput.selected ? setIsChecked(false) : setIsChecked(true);
  };

  const cancelWithValidate = () => {
    if (cancelSurveyEnable && cancelSurveySetting.require && !cancelSurveyInput.selected) {
      MicroModal.close(`modal-cancelled`);
      MicroModal.show('modal-cancelled-error', getModalOption(setting));
      return;
    } else {
      MicroModal.show('modal-cancelled-complete', getModalOption(setting));
    }
    updateStatus('cancelled');
  };

  const closeErrorModal = (status: string) => {
    MicroModal.close(`modal-${status}-error`);
    MicroModal.show(`modal-${status}`, getModalOption(setting));
  };

  const select: JSX.Element[] = cancelSurveySetting.reasons
    ? cancelSurveySetting.reasons.map((reason, index) => {
        return (
          <Fragment key={index}>
            <Input
              type="radio"
              name="reason"
              id={index.toString()}
              value={reason}
              //onChange={() => setCancelSurveyInput({...cancelSurveyInput, selected: reason})}
              onClick={() => setCancelReason(reason)}
              checked={cancelSurveyInput.selected === reason}
            ></Input>
            <Label htmlFor={index.toString()} className={style.selectLabel}>
              {reason}
            </Label>
          </Fragment>
        );
      })
    : [];

  const cancelMainBody = cancelSurveyEnable ? (
    <main id="modal-cancelled-content" className={style.cancelMainBody}>
      {cancelSurveySetting.leadText ? (
        <p>
          {cancelSurveySetting.leadText.split('\n').map((str) => (
            <>
              {str}
              <br />
            </>
          ))}
        </p>
      ) : (
        <></>
      )}
      <div id="selectButton">{select}</div>
      {cancelSurveySetting.extraField ? (
        <>
          <br />
          <h3>
            <b>その他、ご意見やご要望をご記入ください</b>
          </h3>
          <Textarea
            onChange={(e) => setCancelSurveyInput({ ...cancelSurveyInput, extraField: e.target.value })}
            rows={5}
          ></Textarea>
        </>
      ) : (
        <></>
      )}
    </main>
  ) : (
    <main id="modal-cancelled-content">
      <p>定期購買を解約します。</p>
    </main>
  );

  return (
    <>
      <h2 className={style.cancelHeading}>定期便の解約</h2>
      {cancelMainBody}
      <footer className="subscription-cancel-footer">
        <button
          className={style.submitBtn}
          disabled={isChecked ? false : true}
          onClick={() => {
            cancelWithValidate();
          }}
        >
          解約する
        </button>
        <p className={style.note}>※ 既に決済が完了したご注文はキャンセルされません</p>
      </footer>

      {/* 解約完了モーダル */}
      <div className="micromodal" id="modal-cancelled-complete" aria-hidden="true">
        <div className="micromodal-overlay" tabIndex={-1}>
          <div role="dialog" aria-modal="true" aria-labelledby="modal-cancelled-complete-title">
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" data-micromodal-close></button>
              <h2 id="modal-cancelled-complete-title" className={base.ModalTitle}>
                定期購買の解約
              </h2>
            </header>
            <main id="modal-cancelled-complete-content" className={base.ModalContent}>
              <p>
                定期購買を解約しました。
                <br />
                再度ご注文される場合は、もう一度定期購買をご注文ください。
                <br />
              </p>
            </main>
            <footer>
              <button
                className={`${base.sectionBtn} ${base.ModalButton}`}
                data-micromodal-close
                onClick={() => {
                  reactRouterHistory.push(`/`);
                }}
              >
                ＯＫ
              </button>
            </footer>
          </div>
        </div>
      </div>

      {/* 解約エラーモーダル */}
      <div className="micromodal" id="modal-cancelled-error" aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1}>
          <div
            role="dialog"
            className={base.micromodalOverlayDialog}
            aria-modal="true"
            aria-labelledby="modal-cancelled-error-title"
          >
            <main id="modal-cancelled-error-content">
              <h2 id="modal-cancelled-error-title">エラー</h2>
              <p>解約理由を選択してください</p>
            </main>
            <footer>
              <button className={base.sectionBtn} onClick={() => closeErrorModal('cancelled')}>
                ＯＫ
              </button>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
};
